import { useState } from "react";
import {
  applyAttendanceOverride,
  clearAttendanceRecomputeProtection,
  lockAttendanceRow,
  unlockAttendanceRow,
} from "../../../Moduels/attendance/wfApi";

const STATUS_OPTIONS = [
  { value: "", label: "— keep current —" },
  { value: "P", label: "Present (P)" },
  { value: "A", label: "Absent (A)" },
  { value: "HD", label: "Half Day (HD)" },
  { value: "L", label: "Leave (L)" },
  { value: "H", label: "Holiday (H)" },
  { value: "WO", label: "Week Off (WO)" },
];

export default function AttendanceOverrideModal({ attendance, onClose, onSaved }) {
  const [form, setForm] = useState({
    override_status: "",
    override_fraction: "",
    waive_late: false,
    approve_overtime: false,
    protect_from_recompute: true,
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isProtected = attendance?.policy_result_json?.recompute_protected;
  const isLocked = attendance?.is_locked;

  const handleOverride = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) {
      setErr("Reason is required.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = { reason: form.reason, protect_from_recompute: form.protect_from_recompute };
      if (form.override_status) payload.override_status = form.override_status;
      if (form.override_fraction !== "") payload.override_fraction = parseFloat(form.override_fraction);
      if (form.waive_late) payload.waive_late = true;
      if (form.approve_overtime) payload.approve_overtime = true;
      await applyAttendanceOverride(attendance.attendance_id, payload);
      onSaved?.("Override applied.");
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClearProtection = async () => {
    const reason = window.prompt("Reason for clearing recompute protection:");
    if (!reason) return;
    setSaving(true);
    setErr("");
    try {
      await clearAttendanceRecomputeProtection(attendance.attendance_id, reason);
      onSaved?.("Recompute protection cleared.");
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLockToggle = async () => {
    setSaving(true);
    setErr("");
    try {
      if (isLocked) {
        await unlockAttendanceRow(attendance.attendance_id);
        onSaved?.("Row unlocked.");
      } else {
        await lockAttendanceRow(attendance.attendance_id);
        onSaved?.("Row locked for payroll.");
      }
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Attendance Override</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="mb-4 text-sm text-slate-600 bg-slate-50 rounded-lg p-3 space-y-1">
          <div><span className="font-medium">Date:</span> {attendance?.work_date}</div>
          <div><span className="font-medium">Current status:</span> {attendance?.status}</div>
          <div><span className="font-medium">Work hours:</span> {attendance?.work_hours ?? "—"}</div>
          <div className="flex gap-3 mt-1">
            {isLocked && (
              <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                Locked
              </span>
            )}
            {isProtected && (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Recompute protected
              </span>
            )}
          </div>
        </div>

        {err && <div className="mb-3 text-sm text-red-600">{err}</div>}

        <form onSubmit={handleOverride} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Override status</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.override_status}
              onChange={(e) => setForm({ ...form, override_status: e.target.value })}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Override payable fraction <span className="text-slate-400">(0 – 1, blank = keep)</span>
            </label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.25"
              placeholder="e.g. 0.5"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.override_fraction}
              onChange={(e) => setForm({ ...form, override_fraction: e.target.value })}
            />
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.waive_late}
                onChange={(e) => setForm({ ...form, waive_late: e.target.checked })}
              />
              Waive late flag
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.approve_overtime}
                onChange={(e) => setForm({ ...form, approve_overtime: e.target.checked })}
              />
              Approve OT
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.protect_from_recompute}
              onChange={(e) => setForm({ ...form, protect_from_recompute: e.target.checked })}
            />
            Protect from automatic recompute
          </label>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              rows={2}
              placeholder="Required — explain the correction"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-slate-800 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {saving ? "Saving…" : "Apply override"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-lg text-sm"
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={handleLockToggle}
            className={`text-xs px-3 py-1.5 rounded-lg border font-medium disabled:opacity-50 ${
              isLocked
                ? "border-green-300 text-green-700 hover:bg-green-50"
                : "border-red-300 text-red-700 hover:bg-red-50"
            }`}
          >
            {isLocked ? "Unlock row" : "Lock for payroll"}
          </button>
          {isProtected && (
            <button
              type="button"
              disabled={saving}
              onClick={handleClearProtection}
              className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium disabled:opacity-50"
            >
              Clear recompute protection
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
